import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X, Smartphone, Share, PlusSquare, Wifi, WifiOff } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    setIsIOS(/iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase()));
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true);

    const handlePrompt = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    const handleInstalled = () => { setIsInstalled(true); setDeferredPrompt(null); };
    
    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('appinstalled', handleInstalled);
    window.addEventListener('online', () => setIsOnline(true));
    window.addEventListener('offline', () => setIsOnline(false));

    return () => {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setShowDialog(false);
  };

  if (!isOnline) {
    return (
      <div className="fixed bottom-0 left-0 right-0 bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-center gap-2 z-50">
        <WifiOff className="w-4 h-4" />
        <span className="text-sm font-medium">Mode hors-ligne</span>
      </div>
    );
  }

  if (isInstalled || (!deferredPrompt && !isIOS)) return null;

  return (
    <>
      <button
        onClick={() => setShowDialog(true)}
        className="fixed bottom-4 right-4 bg-primary text-primary-foreground rounded-full p-3 shadow-lg z-40 flex items-center gap-2"
      >
        <Download className="w-5 h-5" />
      </button>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="w-5 h-5" /> Installer SecureSign
            </DialogTitle>
            <DialogDescription>
              Installez l'application pour un accès rapide.
            </DialogDescription>
          </DialogHeader>

          {isIOS ? (
            <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950 border">
              <p className="font-medium text-sm mb-3">Pour installer sur iOS :</p>
              <ol className="space-y-2 text-sm">
                <li className="flex items-center gap-2">1. Appuyez sur <Share className="w-4 h-4" /></li>
                <li className="flex items-center gap-2">2. <PlusSquare className="w-4 h-4" /> Sur l'écran d'accueil</li>
                <li>3. Appuyez sur "Ajouter"</li>
              </ol>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm">✅ Accès instantané depuis l'écran d'accueil</p>
              <p className="text-sm">✅ Fonctionne hors-ligne</p>
              <p className="text-sm">✅ Notifications de signature</p>
            </div>
          )}

          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="flex-1">Plus tard</Button>
            {!isIOS && <Button onClick={handleInstall} className="flex-1"><Download className="w-4 h-4 mr-2" />Installer</Button>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
